import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { createLogger } from '@/utils/logger';

const log = createLogger('QueryClient');

/**
 * Checks if an error is a refresh token error (expected auth flow).
 * These errors represent normal session expiration and shouldn't be captured to Sentry.
 */
const isRefreshTokenError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.message.includes('Token refresh failed') || error.message.includes('No refresh token available');
  }
  return false;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: (failureCount, error: unknown) => {
        // Don't retry on auth errors
        if (
          (error as { response?: { status?: number } })?.response?.status === 401 ||
          (error as { response?: { status?: number } })?.response?.status === 403
        ) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        const errorResponse = error as { response?: { status?: number } };
        if (
          errorResponse?.response?.status &&
          errorResponse.response.status >= 400 &&
          errorResponse.response.status < 500
        ) {
          return false; // Don't retry client errors
        }
        return failureCount < 2;
      },
    },
  },
  mutationCache: new MutationCache({
    onError: (error: unknown, _variables, _context, mutation) => {
      const status = (error as { response?: { status?: number } })?.response?.status;

      // Handle auth errors
      if (status === 401) {
        log.debug('Mutation 401 error - dispatching auth:token-expired');
        // Clear auth state and redirect to login
        queryClient.clear();
        // Dispatch event to trigger auth guards and navigation
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return;
      }

      // Don't capture refresh token errors - these are expected auth flows
      if (isRefreshTokenError(error)) {
        log.debug('Refresh token error - not capturing to Sentry');
        return;
      }

      // Capture non-auth errors in Sentry (skip 4xx client errors except for unexpected ones)
      if (!status || status >= 500) {
        Sentry.captureException(error, {
          tags: { errorType: 'mutation_error', mutationKey: String(mutation.options.mutationKey) },
          extra: { status },
        });
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error: unknown, query) => {
      const status = (error as { response?: { status?: number } })?.response?.status;

      // Handle auth errors
      if (status === 401) {
        log.debug('Query 401 error - dispatching auth:token-expired');
        // Clear auth state and redirect to login
        queryClient.clear();
        // Dispatch event to trigger auth guards and navigation
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return;
      }

      // Don't capture refresh token errors - these are expected auth flows
      if (isRefreshTokenError(error)) {
        log.debug('Refresh token error - not capturing to Sentry');
        return;
      }

      // Capture non-auth errors in Sentry (skip 4xx client errors except for unexpected ones)
      if (!status || status >= 500) {
        Sentry.captureException(error, {
          tags: { errorType: 'query_error', queryKey: String(query.queryKey) },
          extra: { status },
        });
      }
    },
  }),
});
